import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBillingStatus, type BillingStatus } from '@auw/api-client';
import { queryKeys } from '@auw/workspace-utils';
import { parseApiError } from '../utils';

interface UseBillingStatusOptions {
  fallbackErrorMessage: string;
  initialFetch?: boolean;
  syncOnFocusVisible?: boolean;
}

export const useBillingStatus = ({
  fallbackErrorMessage,
  initialFetch = true,
  syncOnFocusVisible = true,
}: UseBillingStatusOptions) => {
  const query = useQuery<BillingStatus>({
    queryKey: queryKeys.billing.status(),
    queryFn: getBillingStatus,
    enabled: initialFetch,
    refetchOnWindowFocus: syncOnFocusVisible,
  });

  const refresh = useCallback(async () => {
    await query.refetch();
  }, [query.refetch]);

  const error = useMemo(() => {
    if (!query.error) return null;
    return parseApiError(query.error, fallbackErrorMessage);
  }, [query.error, fallbackErrorMessage]);

  return {
    billing: query.data ?? null,
    loading: query.isFetching,
    error,
    refresh,
  };
};
